import * as React from 'react';
import { Component, Fragment } from 'react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import {
    propStyles,
    style,
} from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { verticalAlign } from '../utils/customPropStyles';
import { createPopper } from '@popperjs/core';
import Icon from './Icon';
import ClickLayer from './ClickLayer';

const arrowFill = style({
    prop: 'bgColor',
    key: 'color',
    cssProperty: 'fill',
});

const arrowStroke = style({
    prop: 'borderColor',
    key: 'color',
    cssProperty: 'stroke',
});

const ArrowIcon = () => (
    <svg width="24px" height="24px" viewBox="0 0 24 24">
        <path className="arrow__path" d="M7,10L12,15L17,10H7Z" />
    </svg>
);

const Arrow = styled<any, any>('div')`
    position: absolute;
    height: 24px;
    width: 24px;
    .arrow__path {
        fill: inherit;
        stroke: inherit;
        stroke-width: 1px;
        stroke-dasharray: 14, 7, 0;
    }
`;

const TooltipWrapper = styled<any, any>('div')`
    ${propStyles};
    ${verticalAlign};
`;

TooltipWrapper.defaultProps = {
    display: 'inline-block',
};

const TooltipIconButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
`;

TooltipIconButton.defaultProps = {
    display: 'inline',
    color: 'primary',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
};

const TooltipIcon = styled(Icon)``;

TooltipIcon.defaultProps = {
    height: '16px',
    width: '16px',
};

const TooltipCharacter = styled<any, any>('div')`
    ${propStyles};
`;

TooltipCharacter.defaultProps = {
    fontSize: '9px',
    minWidth: '1.5em',
    margin: '0 0.5em',
    padding: '0.15em 0.25em',
    color: 'white',
    bgColor: 'primary',
    borderRadius: '1.5em',
    textAlign: 'center',
};

const TooltipContent = styled<any, any>('div')`
    ${propStyles};
    cursor: pointer;

    ${Arrow} {
        ${arrowFill};
        ${arrowStroke};
        transform-origin: center;
    }

    &[data-popper-placement='top'] ${Arrow} {
        bottom: -14px;
    }

    &[data-popper-placement='bottom'] ${Arrow} {
        top: -14px;
        transform: rotate(180deg);
    }

    &[data-popper-placement='left'] ${Arrow} {
        right: -14px;
        transform: rotate(-90deg);
    }

    &[data-popper-placement='right'] ${Arrow} {
        left: -14px;
        transform: rotate(90deg);
    }
`;

TooltipContent.defaultProps = {
    minWidth: '200px',
    maxWidth: '300px',
    color: 'body',
    bgColor: 'white',
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'center',
    padding: 'sm',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray500',
    borderRadius: 'sm',
    boxShadow: 'sm',
    zIndex: 'popover',
};

interface TooltipProps {
    theme?: { [property: string]: string | number };
    tooltipCharacter?: string;
    showContent?: boolean;
    content: Array<string>;
    verticalAlign?: string;
    accessibilityContext?: string;
}

@observer
class Tooltip extends Component<TooltipProps> {
    popper;
    tooltipNode;
    tooltipContentNode;
    arrowNode;
    @observable show = false;

    initPopper = () => {
        if (this.tooltipNode && this.tooltipContentNode) {
            this.popper = createPopper(
                this.tooltipNode,
                this.tooltipContentNode,
                {
                    placement: 'top',
                    modifiers: [
                        {
                            name: 'arrow',
                            options: {
                                element: this.arrowNode,
                            },
                        },
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 5],
                            },
                        },
                    ],
                }
            );
        }
    };

    componentDidMount() {
        this.initPopper();
    }

    componentDidUpdate() {
        this.initPopper();
    }

    setTooltipNode = node => {
        this.tooltipNode = node;
    };

    setTooltipContentNode = node => {
        this.tooltipContentNode = node;
    };

    setArrowNode = node => {
        this.arrowNode = node;
    };

    toggleTooltip = e => {
        e.stopPropagation();
        this.show = !this.show;
        if (this.popper) {
            this.popper.update();
        }
    };

    @computed
    get content() {
        return this.props.content || [];
    }

    @computed
    get hasContent() {
        return this.content.length > 0;
    }

    render() {
        const {
            theme,
            tooltipCharacter,
            showContent = true,
            accessibilityContext,
            ...props
        } = this.props;

        const { content, hasContent } = this;

        if (hasContent) {
            return (
                <TooltipWrapper {...props}>
                    <TooltipIconButton
                        ref={this.setTooltipNode}
                        onClick={showContent ? this.toggleTooltip : () => null}
                        cursor={showContent ? 'pointer' : 'default'}
                        {...theme.TooltipIconButton}
                    >
                        {tooltipCharacter ? (
                            <TooltipCharacter {...theme.TooltipCharacter}>
                                {tooltipCharacter}
                            </TooltipCharacter>
                        ) : (
                            <TooltipIcon
                                fileName="InformationIcon"
                                {...theme.TooltipIcon}
                                accessibilityContext={accessibilityContext}
                            />
                        )}
                    </TooltipIconButton>
                    {showContent && (
                        <Fragment>
                            {this.show && (
                                <ClickLayer onClick={this.toggleTooltip} />
                            )}
                            {
                                <TooltipContent
                                    ref={this.setTooltipContentNode}
                                    onClick={this.toggleTooltip}
                                    {...theme.TooltipContent}
                                    style={{
                                        display: this.show ? 'block' : 'none',
                                    }}
                                >
                                    {content.map((c, index) => (
                                        <div
                                            key={index}
                                            dangerouslySetInnerHTML={{
                                                __html: c,
                                            }}
                                        />
                                    ))}
                                    <Arrow
                                        ref={this.setArrowNode}
                                        className="popper__arrow"
                                        x-arrow=""
                                    >
                                        <ArrowIcon />
                                    </Arrow>
                                </TooltipContent>
                            }
                        </Fragment>
                    )}
                </TooltipWrapper>
            );
        }

        return null;
    }
}

export default withTheme(Tooltip);



// WEBPACK FOOTER //
// ./src/Components/Tooltip.tsx